import { useManagees } from "~/components/team-member/composables/useManagees";
import { useUserMe } from "~/components/user/composables/useUser";
import { useWorkspaceMe } from "~/components/workspace/composables/useWorkspace";
import { useFeatureFlag } from "~/composables/useFeatureFlag";
import type { Color } from "@asap/design/styles/colors";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import { intersection } from "lodash-es";

export type NavButton = {
  to: string;
  // Route prefix that marks the button active when `to` points to a sub-page, e.g. a V2 list
  activePath?: string;
  icon: string;
  label: string;
  roles: AppRole[];
  isHidden?: boolean;
  isChild?: boolean;
  isParent?: boolean;
  isFolded?: boolean;
  trigger?: () => void;
  count?: number;
};

export type NavButtonSection = {
  name: string;
  icon: string;
  color: Color;
  isHidden?: boolean;
  items: NavButton[];
};

export const getNavButtonActivePath = (button: NavButton) => button.activePath ?? button.to.split("?")[0];

export const useNavBarData = () => {
  const { t } = useI18n();
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const { userRoles } = useUserMe();
  const { isIndependentWorkspace } = useWorkspaceMe();
  const { isCountryManager } = useManagees();

  const isMencoEnabled = isFeatureFlagEnabled("menco");
  const isJobMatrixClassificationEnabled = isFeatureFlagEnabled("job-matrix-classification");
  const isThirdPartyNoticesEnabled = isFeatureFlagEnabled("third-party-notices");
  const isRecruitmentOfficeInvoiceRequestV2Enabled = isFeatureFlagEnabled("recruitment-office-invoice-request-v2");

  const defaultNavButtonSections = computed<NavButtonSection[]>(() => [
    {
      name: t("navigation.sections.talentTracking"),
      icon: "i-ph-users-fill",
      color: "blue",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/ats",
          icon: "i-ph-address-book",
          label: t("navigation.items.talents"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/applications",
          icon: "i-ph-note",
          label: t("navigation.items.applications"),
          roles: ["consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/talents/deep-research/",
          icon: "i-ph-cloud",
          label: t("navigation.items.deepResearch"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/talents/push-marketing",
          icon: "i-ph-envelope-simple",
          label: t("navigation.items.pushMarketing"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member"],
        },
        {
          to: "/reference-checks",
          icon: "i-ph-seal-check",
          label: t("navigation.items.referenceChecks"),
          roles: ["consultant_team_member"],
        },
      ],
    },
    {
      name: t("navigation.sections.prospecting"),
      icon: "i-ph-briefcase-fill",
      color: "green",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/missions/client-order",
          icon: "i-ph-target",
          label: t("navigation.items.clientOrders"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/missions/talent-pooling",
          icon: "i-ph-binoculars",
          label: t("navigation.items.talentPooling"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/companies",
          icon: "i-ph-buildings",
          label: t("navigation.items.companies"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/contacts",
          icon: "i-ph-user-plus",
          label: t("navigation.items.contacts"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/commercial-conditions",
          icon: "i-ph-hand-heart",
          label: t("navigation.items.commercialConditions"),
          roles: ["consultant_team_member"],
        },
      ],
    },
    {
      name: t("navigation.sections.contractTracking"),
      icon: "i-ph-file-text-fill",
      color: "red",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/contracts",
          icon: "i-ph-file-arrow-down",
          label: t("navigation.items.contracts"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/amendments",
          icon: "i-ph-files",
          label: t("navigation.items.amendments"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member"],
        },
        {
          to: "/planning",
          icon: "i-ph-calendar-dot",
          label: t("navigation.items.planning"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/timesheets",
          icon: "i-ph-clock-countdown",
          label: t("navigation.items.timesheets"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member"],
        },
        {
          to: isRecruitmentOfficeInvoiceRequestV2Enabled.value
            ? "/recruitment-office-invoice-requests/v2"
            : "/recruitment-office-invoice-requests",
          activePath: "/recruitment-office-invoice-requests",
          icon: "i-ph-currency-circle-dollar",
          label: t("navigation.items.recruitmentOfficeInvoiceRequests"),
          roles: ["accounting_team_member", "consultant_team_member"],
        },
        {
          to: "/pay-tracking",
          icon: "i-ph-money",
          label: t("navigation.items.payTracking"),
          roles: ["accounting_team_member", "consultant_team_member", "csm_team_member"],
        },
        {
          to: "/third-party-notices",
          icon: "i-ph-envelope-simple-open",
          label: t("navigation.items.thirdPartyNotices"),
          roles: ["csm_team_member"],
          isHidden: !isThirdPartyNoticesEnabled.value,
        },
      ],
    },
    {
      name: t("navigation.sections.toolbox"),
      icon: "i-ph-toolbox-fill",
      color: "blue",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/team-members",
          icon: "i-ph-address-book",
          label: t("navigation.items.teamMembers"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member"],
        },
        {
          to: "/calculators",
          icon: "i-ph-numpad",
          label: t("navigation.items.calculators"),
          roles: ["consultant_team_member", "csm_team_member", "accounting_team_member", "team_member_marketing"],
        },
        {
          to: "/cooptation",
          icon: "i-ph-handshake",
          isHidden: isIndependentWorkspace.value,
          label: t("navigation.items.cooptations"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member"],
        },
        {
          to: "/convert",
          icon: "i-ph-arrows-counter-clockwise",
          label: t("navigation.items.convert"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/job-matrix",
          icon: "i-ph-tree-structure",
          label: t("navigation.items.jobMatrix"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/job-matrix/classification",
          icon: "i-ph-list-checks",
          label: t("navigation.items.jobMatrixClassification"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
          isHidden: !isJobMatrixClassificationEnabled.value,
        },
      ],
    },
    {
      name: t("navigation.sections.requestTracking"),
      icon: "i-ph-clipboard-text-fill",
      color: "orange",
      items: [
        {
          to: "/ppe-requests",
          icon: "i-ph-hard-hat",
          label: t("navigation.items.ppeRequests"),
          roles: ["csm_team_member", "consultant_team_member"],
        },
        {
          to: "/definitive-end-requests",
          icon: "i-ph-file-x",
          label: t("navigation.items.definitiveEndRequests"),
          roles: ["accounting_team_member", "consultant_team_member", "csm_team_member"],
          isHidden: isIndependentWorkspace.value || userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/work-accident-requests",
          icon: "i-ph-hammer",
          label: t("navigation.items.workAccidentRequests"),
          roles: ["csm_team_member", "consultant_team_member", "accounting_team_member"],
          isHidden: userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/medical-checkup-requests",
          icon: "i-ph-stethoscope",
          label: t("navigation.items.medicalCheckupRequests"),
          roles: ["csm_team_member", "consultant_team_member", "accounting_team_member"],
        },
        {
          to: "/medical-centers",
          icon: "i-ph-first-aid-kit",
          label: t("navigation.items.medicalCenters"),
          roles: ["csm_team_member", "team_member_developer"],
        },
        {
          to: "/professional-card-requests",
          icon: "i-ph-identification-card",
          label: t("navigation.items.professionalCardRequests"),
          roles: ["accounting_team_member", "consultant_team_member", "csm_team_member"],
        },
        {
          to: "/advance-payment-requests",
          icon: "i-ph-piggy-bank",
          label: t("navigation.items.advancePaymentRequests"),
          roles: [
            "accounting_team_member",
            "csm_team_member",
            "consultant_team_member",
            "team_member_developer",
            "team_member_independent_administrator",
          ],
          isHidden: userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/qualification-training-requests",
          icon: "i-ph-seal-check",
          label: t("navigation.items.qualificationTrainingRequests"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member"],
        },
        {
          to: "/qualification-centers",
          icon: "i-ph-student",
          label: t("navigation.items.qualificationCenters"),
          roles: ["csm_team_member", "team_member_developer"],
          isHidden: userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/expense-claim-requests",
          icon: "i-ph-hand-coins",
          label: t("navigation.items.expenseClaimRequests"),
          roles: ["accounting_team_member", "consultant_team_member", "csm_team_member"],
          isHidden: userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/meal-card-requests",
          icon: "i-ph-cardholder",
          label: t("navigation.items.mealCardRequests"),
          roles: ["csm_team_member", "team_member_developer", "accounting_team_member"],
          isHidden: userRoles.value.includes("external_csm_team_member"),
        },
        {
          to: "/independent-payment-requests",
          icon: "i-ph-newspaper-clipping",
          label: t("navigation.items.independentPaymentRequests"),
          isHidden:
            (!isIndependentWorkspace.value && !userRoles.value.includes("accounting_team_member")) ||
            userRoles.value.includes("external_csm_team_member"),
          roles: ["accounting_team_member", "consultant_team_member"],
        },
      ],
    },
    {
      name: t("navigation.sections.developer"),
      icon: "i-ph-code-fill",
      color: "green",
      isHidden:
        !userRoles.value.some((role) =>
          ["team_member_developer", "team_member_independent_administrator"].includes(role)
        ) && !isCountryManager.value,
      items: [
        {
          to: "/users",
          icon: "i-ph-users",
          label: t("navigation.items.users"),
          roles: ["team_member_developer"],
        },
        {
          to: "/organigram",
          icon: "i-ph-git-branch",
          label: t("navigation.items.organigram"),
          roles: ["team_member_developer", "team_member_independent_administrator", "consultant_team_member"],
        },
      ],
    },
  ]);

  const mencoNavButtonSections = computed<NavButtonSection[]>(() => [
    {
      name: t("navigation.sections.talentTracking"),
      icon: "i-ph-users-fill",
      color: "blue",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/ats",
          icon: "i-ph-address-book",
          label: t("navigation.items.talents"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/applications",
          icon: "i-ph-note",
          label: t("navigation.items.applications"),
          roles: ["consultant_team_member", "team_member_marketing"],
        },
      ],
    },
    {
      name: t("navigation.sections.prospecting"),
      icon: "i-ph-briefcase-fill",
      color: "green",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/missions/client-order",
          icon: "i-ph-target",
          label: t("navigation.items.clientOrders"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/companies",
          icon: "i-ph-buildings",
          label: t("navigation.items.companies"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
        {
          to: "/contacts",
          icon: "i-ph-user-plus",
          label: t("navigation.items.contacts"),
          roles: ["consultant_team_member", "accounting_team_member", "csm_team_member", "team_member_marketing"],
        },
      ],
    },
    {
      name: t("navigation.sections.contractTracking"),
      icon: "i-ph-file-text-fill",
      color: "red",
      isHidden: userRoles.value.includes("external_csm_team_member"),
      items: [
        {
          to: "/contracts",
          icon: "i-ph-file-arrow-down",
          label: t("navigation.items.contracts"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
        {
          to: "/amendments",
          icon: "i-ph-files",
          label: t("navigation.items.amendments"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member"],
        },
        {
          to: "/planning",
          icon: "i-ph-calendar-dot",
          label: t("navigation.items.planning"),
          roles: ["accounting_team_member", "csm_team_member", "consultant_team_member", "team_member_marketing"],
        },
      ],
    },
    {
      name: t("navigation.sections.requestTracking"),
      icon: "i-ph-clipboard-text-fill",
      color: "orange",
      items: [
        {
          to: "/professional-card-requests",
          icon: "i-ph-identification-card",
          label: t("navigation.items.professionalCardRequests"),
          roles: ["accounting_team_member", "consultant_team_member", "csm_team_member"],
        },
        {
          to: "/medical-checkup-requests",
          icon: "i-ph-stethoscope",
          label: t("navigation.items.medicalCheckupRequests"),
          roles: ["csm_team_member", "consultant_team_member", "accounting_team_member"],
        },
      ],
    },
    {
      name: t("navigation.sections.developer"),
      icon: "i-ph-code-fill",
      color: "green",
      isHidden:
        !userRoles.value.some((role) =>
          ["team_member_developer", "team_member_independent_administrator"].includes(role)
        ) && !isCountryManager.value,
      items: [
        {
          to: "/users",
          icon: "i-ph-users",
          label: t("navigation.items.users"),
          roles: ["team_member_developer"],
        },
        {
          to: "/organigram",
          icon: "i-ph-git-branch",
          label: t("navigation.items.organigram"),
          roles: ["team_member_developer", "team_member_independent_administrator", "consultant_team_member"],
        },
      ],
    },
  ]);

  const baseNavButtonSections = computed<NavButtonSection[]>(() =>
    isMencoEnabled.value ? mencoNavButtonSections.value : defaultNavButtonSections.value
  );

  const navButtonSectionsFiltered = computed(() =>
    baseNavButtonSections.value
      .filter((section) => !section.isHidden)
      .map((section) => ({
        ...section,
        items: section.items
          .filter((item) => intersection(userRoles.value, item.roles).length)
          .filter((item) => !item.isHidden),
      }))
  );

  const isRouteInNav = (path: string): boolean => {
    for (const section of baseNavButtonSections.value) {
      for (const item of section.items) {
        if (path.startsWith(getNavButtonActivePath(item))) {
          return true;
        }
      }
    }
    return false;
  };

  const isExactNavRoute = (path: string): boolean => {
    const basePath = path.split("?")[0];

    for (const section of baseNavButtonSections.value) {
      for (const item of section.items) {
        if (basePath === item.to) {
          return true;
        }

        if (item.to.endsWith("/") && basePath.startsWith(item.to)) {
          const remainingPath = basePath.substring(item.to.length);
          if (remainingPath === "" || !/^\/\d+(\/|$)/.test(remainingPath)) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const getSectionForRoute = (path: string): { section: NavButtonSection; item: NavButton } | null => {
    const basePath = path.split("?")[0];

    for (const section of baseNavButtonSections.value) {
      for (const item of section.items) {
        if (basePath.startsWith(getNavButtonActivePath(item))) {
          return { section, item };
        }
      }
    }

    for (const section of baseNavButtonSections.value) {
      for (const item of section.items) {
        if (
          (item.to === "/ats" && basePath.startsWith("/talents/")) ||
          (item.to.includes("/missions/") && basePath.startsWith("/missions/")) ||
          (item.to.includes("/contracts/") && basePath.startsWith("/contracts/"))
        ) {
          return { section, item };
        }
      }
    }

    return null;
  };

  const getCurrentSection = (): { section: NavButtonSection; item: NavButton } | null => {
    const route = useRoute();
    return getSectionForRoute(route.path);
  };

  return {
    baseNavButtonSections,
    navButtonSectionsFiltered,
    getCurrentSection,
    isRouteInNav,
    isExactNavRoute,
    getSectionForRoute,
  };
};
